/**
 * Bumper-car appearance — the player's car design (owner request, 2026-07-21).
 *
 * Every car shares ONE silhouette (fairness: identical physics footprint); the
 * design only changes colours and flat decorations. The geometry source of
 * truth is the owner's SVG component (viewBox 144×204, nose toward −y): the
 * client renders it (see client/src/car/svg.ts) and the physics capsule in
 * collision.ts is its quasi-exact hull scaled to world units.
 *
 * This module is pure data + pure functions so the server (validation,
 * persistence, livery merge) and the client (configurator, renderer) agree on
 * a single definition. The wire/DB validation schema lives in protocol.ts.
 */

import type { Livery } from './league';

/** Stripe patterns painted on the body (clipped to it), from the design sheet. */
export const CAR_STRIPE_STYLES = [
  'none',
  'central',
  'large',
  'checker',
  'nose',
  'half',
  'flames',
  'chevrons',
  'dots',
  'diag',
  'sides',
  'tail',
  'rays',
  'ring',
  'horseshoe',
  'cross',
  'bars',
  'target',
  'comet',
  'petals',
  'orbit',
] as const;
export type CarStripeStyle = (typeof CAR_STRIPE_STYLES)[number];

/** Flat motifs available on the roof disc. */
export const CAR_MOTIFS = [
  'star',
  'bolt',
  'crown',
  'heart',
  'diamond',
  'clover',
  'moon',
  'spade',
  'arrow',
  'shield',
  'skull',
  'paw',
  'dot',
] as const;
export type CarMotif = (typeof CAR_MOTIFS)[number];

/** Roof disc content: nothing, a 2-char number/letters, a flag, or a motif. */
export const CAR_DECALS = ['none', 'number', 'flag', ...CAR_MOTIFS] as const;
export type CarDecal = (typeof CAR_DECALS)[number];

/** Simplified flags of the design sheet. */
export const CAR_FLAGS = ['fr', 'it', 'de', 'es', 'gb', 'jp', 'br', 'us'] as const;
export type CarFlag = (typeof CAR_FLAGS)[number];

export interface CarConfig {
  /** Body colour (any #rrggbb). */
  body: string;
  /** Rubber bumper ring colour. */
  rubber: string;
  /** Stripe colour. */
  stripe: string;
  stripeStyle: CarStripeStyle;
  /** Windshield tint. */
  glass: string;
  spoiler: boolean;
  spoilerColor: string;
  decal: CarDecal;
  /** 0-2 chars, digits or letters, upper-cased (e.g. "7", "R1"). */
  number: string;
  flag: CarFlag;
  /** Roof disc background. The number/motif ink is NOT configurable: it is
   * black or cream automatically from this colour's luminance (decalInk). */
  decalColor: string;
}

// ---------------------------------------------------------------------------
// Render geometry (shared so the renderer and the debug hull agree)
// ---------------------------------------------------------------------------

/**
 * The SVG component's viewBox and the on-track size. `scale` converts viewBox
 * units to world px: the drawn car is CAR_LENGTH_PX long — and the physics
 * capsule of collision.ts is the hull's capsule (±31.5, r 70.5 in viewBox
 * units) at this very scale, rounded to (±4, r 9) world px.
 */
export const CAR_VIEW = { w: 144, h: 204 } as const;
export const CAR_LENGTH_PX = 26;
export const CAR_WORLD_SCALE = CAR_LENGTH_PX / CAR_VIEW.h;

// ---------------------------------------------------------------------------
// Palettes (design sheet) — shared by the configurator and the randomizer
// ---------------------------------------------------------------------------

export const CAR_INK = '#20222e';
const CREAM = '#f7f3e8';

export const CAR_BODY_PALETTE = [
  '#ef3b4f',
  '#ff8a2a',
  '#ffc93c',
  '#35c56f',
  '#17c3b2',
  '#2f7de1',
  '#8e5be8',
  '#ff6bb5',
  '#f5f2ea',
  '#23242e',
] as const;

export const CAR_STRIPE_PALETTE = [
  CREAM,
  '#23242e',
  '#ff8a2a',
  '#ffc93c',
  '#35c56f',
  '#17c3b2',
  '#2f7de1',
  '#8e5be8',
  '#ff6bb5',
  '#ef3b4f',
] as const;

export const CAR_GLASS_PALETTE = ['#262b3d', '#3c5f8f', '#54427e'] as const;
export const CAR_SPOILER_PALETTE = [CAR_INK, CREAM, '#ef3b4f', '#ffc93c'] as const;
export const CAR_RUBBER_PALETTE = [
  CAR_INK,
  CREAM,
  '#ef3b4f',
  '#ffc93c',
  '#2f7de1',
  '#17c3b2',
] as const;

// ---------------------------------------------------------------------------
// Construction / normalization
// ---------------------------------------------------------------------------

/** The design sheet's default car, optionally re-painted with a body colour. */
export function defaultCarConfig(body = '#2f7de1'): CarConfig {
  return {
    body: normalizeHex(body) ?? '#2f7de1',
    rubber: CAR_INK,
    stripe: CREAM,
    stripeStyle: 'central',
    glass: '#262b3d',
    spoiler: true,
    spoilerColor: CAR_INK,
    decal: 'number',
    number: '7',
    flag: 'fr',
    decalColor: CREAM,
  };
}

/** Lower-cased #rrggbb, or null when the value is not one. */
export function normalizeHex(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : null;
}

/** Upper-cases and strips a roof number down to its 0-2 legal characters. */
export function normalizeCarNumber(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 2);
}

/**
 * Structural validation of an untrusted value (DB reads, defensive parsing).
 * Returns a fully normalized config, or null when the shape is not usable.
 * The wire schema (zod, protocol.ts) accepts the same set of values.
 */
export function sanitizeCarConfig(raw: unknown): CarConfig | null {
  if (raw === null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const body = normalizeHex(o.body);
  const rubber = normalizeHex(o.rubber);
  const stripe = normalizeHex(o.stripe);
  const glass = normalizeHex(o.glass);
  const spoilerColor = normalizeHex(o.spoilerColor);
  const decalColor = normalizeHex(o.decalColor);
  if (!body || !rubber || !stripe || !glass || !spoilerColor || !decalColor) return null;
  if (!includes(CAR_STRIPE_STYLES, o.stripeStyle)) return null;
  if (!includes(CAR_DECALS, o.decal)) return null;
  if (!includes(CAR_FLAGS, o.flag)) return null;
  if (typeof o.spoiler !== 'boolean') return null;
  return {
    body,
    rubber,
    stripe,
    stripeStyle: o.stripeStyle,
    glass,
    spoiler: o.spoiler,
    spoilerColor,
    decal: o.decal,
    number: normalizeCarNumber(o.number),
    flag: o.flag,
    decalColor,
  };
}

function includes<T extends string>(list: readonly T[], v: unknown): v is T {
  return typeof v === 'string' && (list as readonly string[]).includes(v);
}

/**
 * Random design with the design sheet's distributions (number twice as likely
 * as a flag, dark rubber twice as likely as a coloured one…). `rand` is an
 * injected [0,1) source so tests can drive it deterministically.
 */
export function randomCarConfig(rand: () => number): CarConfig {
  const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)] as T;
  return {
    body: pick(CAR_BODY_PALETTE),
    stripe: pick(CAR_STRIPE_PALETTE),
    stripeStyle: pick(CAR_STRIPE_STYLES),
    glass: pick(CAR_GLASS_PALETTE),
    spoiler: rand() > 0.25,
    spoilerColor: pick(CAR_SPOILER_PALETTE),
    decal: pick(['number', 'number', 'flag', ...CAR_MOTIFS] as const),
    number: String(1 + Math.floor(rand() * 98)),
    flag: pick(CAR_FLAGS),
    rubber: pick([CAR_INK, ...CAR_RUBBER_PALETTE] as const),
    decalColor: pick([CREAM, CREAM, CAR_INK, '#ef3b4f', '#ffc93c', '#2f7de1', '#17c3b2'] as const),
  };
}

/**
 * Roof ink contrast rule of the SVG component: black on a light disc, cream on
 * a dark one — the ink is never a free parameter. Exact formula of the design
 * file (BT.601 luma over the disc colour, threshold 145; unparsable → light).
 */
export function decalInk(decalColor: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(decalColor);
  const lum = m
    ? 0.299 * parseInt((m[1] as string).slice(0, 2), 16) +
      0.587 * parseInt((m[1] as string).slice(2, 4), 16) +
      0.114 * parseInt((m[1] as string).slice(4, 6), 16)
    : 255;
  return lum > 145 ? CAR_INK : CREAM;
}

// ---------------------------------------------------------------------------
// Livery overlay (leagues, Phase 6) — a worn livery re-paints the design
// ---------------------------------------------------------------------------

/**
 * How a champion's unlocked livery expresses on the bumper car. The livery
 * system predates the design system: its patterns are mapped onto stripe
 * styles so a worn livery stays instantly recognizable everywhere, while the
 * pilot keeps their roof decal/number and spoiler.
 *
 * - solid    → plain body in c0
 * - stripes  → body c0, double central stripes in c1
 * - halves   → body c0, half in c1
 * - tricolor → nose in c0, body c1, RUBBER ring in c2 (the French champion's
 *              Tricolore reads blue nose / white body / red rubber)
 */
export function applyLiveryToCarConfig(design: CarConfig, livery: Livery | null): CarConfig {
  if (!livery) return design;
  const c0 = normalizeHex(livery.colors[0]) ?? design.body;
  const c1 = normalizeHex(livery.colors[1]) ?? c0;
  const c2 = normalizeHex(livery.colors[2]) ?? c1;
  switch (livery.pattern) {
    case 'solid':
      return { ...design, body: c0, stripeStyle: 'none' };
    case 'stripes':
      return { ...design, body: c0, stripe: c1, stripeStyle: 'central' };
    case 'halves':
      return { ...design, body: c0, stripe: c1, stripeStyle: 'half' };
    case 'tricolor':
      return { ...design, body: c1, stripe: c0, stripeStyle: 'nose', rubber: c2 };
    default:
      return design;
  }
}
